import React, { useMemo, useState, useCallback } from "react";
import ReactFlow, {
  Background,
  Controls,
  Position,
} from "react-flow-renderer";
import dagre from "dagre";
import "react-flow-renderer/dist/style.css";

const nodeWidth = 220;
const nodeHeight = 70;

const TYPE_CONFIG = {
  root: {
    label: "Root",
    color: "#1E88E5",
    bg: "#EBF5FE",
    border: "#64B5F6",
    badgeColor: "#64B5F6",
  },
  main_category: {
    label: "Casual Factor",
    color: "#D81B60",
    bg: "#FFF0F3",
    border: "#FF8DA1",
    badgeColor: "#FF8DA1",
  },
  sub_category: {
    label: "Problem Category",
    color: "#E65100",
    bg: "#FFF8EE",
    border: "#FFC078",
    badgeColor: "#FFC078",
  },
  major_root_cause_category: {
    label: "Major Root Cause",
    color: "#00695C",
    bg: "#E6F9F9",
    border: "#80D8DA",
    badgeColor: "#80D8DA",
  },
  near_root_cause_category: {
    label: "Near Root Cause",
    color: "#1565C0",
    bg: "#EEF7FE",
    border: "#64B5F6",
    badgeColor: "#64B5F6",
  },
  intermediate_cause_category: {
    label: "Immediate Cause",
    color: "#AD1457",
    bg: "#FFF0F5",
    border: "#FF8DA1",
    badgeColor: "#FF8DA1",
  },
  root_cause_type: {
    label: "Root Cause Factor",
    color: "#00796B",
    bg: "#E6F9F9",
    border: "#80D8DA",
    badgeColor: "#80D8DA",
  },
  root_cause: {
    label: "Root Cause Detail",
    color: "#E65100",
    bg: "#FFF8EE",
    border: "#FFC078",
    badgeColor: "#FFC078",
  },
};

// Embedded CSS Animations
const animationStyles = `
@keyframes clcFloatAnimation {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-4px); }
  100% { transform: translateY(0px); }
}

@keyframes clcPulseGlow {
  0% { box-shadow: 0 0 0 0 rgba(100, 181, 246, 0.4); }
  70% { box-shadow: 0 0 0 12px rgba(100, 181, 246, 0); }
  100% { box-shadow: 0 0 0 0 rgba(100, 181, 246, 0); }
}

.clc-node-card {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease, border-color 0.3s ease !important;
}

.clc-node-card:hover {
  transform: translateY(-6px) scale(1.03) !important;
  box-shadow: 0 10px 20px -4px rgba(0, 0, 0, 0.12) !important;
}

.clc-root-pulse {
  animation: clcPulseGlow 3s infinite ease-in-out;
}

.clc-toggle-btn {
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease !important;
}

.clc-toggle-btn:hover {
  transform: translateX(-50%) scale(1.3) !important;
}

.react-flow__edge-path {
  stroke-dasharray: 6;
  animation: clcDashFlow 1s linear infinite;
}

@keyframes clcDashFlow {
  from { stroke-dashoffset: 24; }
  to { stroke-dashoffset: 0; }
}
`;

function getLayoutedElements(nodes, edges) {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: "TB", ranksep: 70, nodesep: 50 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const pos = dagreGraph.node(node.id);
    if (pos) {
      node.position = {
        x: pos.x - nodeWidth / 2,
        y: pos.y - nodeHeight / 2,
      };
    }
  });

  return { nodes, edges };
}

function getDescendantIds(nodeId, childrenMap) {
  const result = [];
  const queue = [...(childrenMap[nodeId] || [])];
  while (queue.length) {
    const current = queue.shift();
    result.push(current);
    (childrenMap[current] || []).forEach((child) => queue.push(child));
  }
  return result;
}

const getLabel = (item, keys) => {
  if (!item) return "";
  for (const k of keys) {
    if (item[k] && typeof item[k] === "string") return item[k];
  }
  return "";
};

export default function CLCHierarchyChart({ step4Data, isRoot = true, rootName = "" }) {
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());

  // Parse step4Data JSON into hierarchy node objects
  const hierarchyNodes = useMemo(() => {
    const nodesList = [];
    if (!step4Data) return nodesList;

    const contributingFactor = step4Data?.contributing_factor || step4Data;

    if (isRoot) {
      // Main Category Hierarchy (Casual Factor Analysis)
      const mainCategories = contributingFactor?.main_category || step4Data?.main_category || [];
      if (!mainCategories.length) return nodesList;

      const rootId = "root_main";
      nodesList.push({
        id: rootId,
        type: "root",
        parentNodeId: null,
        title: rootName !== "" ? rootName : step4Data?.probable_cause?.probable_cause || "Casual Factor Analysis",
      });

      mainCategories.forEach((main, mIdx) => {
        const mainLabel = getLabel(main, ["main_category", "name", "title", "label"]);
        if (!mainLabel) return;
        const mainId = `main_${main.main_category_id || mIdx}`;
        nodesList.push({
          id: mainId,
          type: "main_category",
          parentNodeId: rootId,
          title: mainLabel,
        });

        (main.sub_category || []).forEach((sub, sIdx) => {
          const subLabel = getLabel(sub, ["sub_category", "name", "title", "label"]);
          if (!subLabel) return;
          const subId = `sub_${sub.sub_category_id || `${mIdx}_${sIdx}`}`;
          nodesList.push({
            id: subId,
            type: "sub_category",
            parentNodeId: mainId,
            title: subLabel,
          });

          (sub.major_root_cause_category || []).forEach((major, majIdx) => {
            const majorLabel = getLabel(major, ["major_root_cause_category", "name", "title", "label"]);
            if (!majorLabel) return;
            const majorId = `maj_${major.major_root_cause_category_id || `${mIdx}_${sIdx}_${majIdx}`}`;
            nodesList.push({
              id: majorId,
              type: "major_root_cause_category",
              parentNodeId: subId,
              title: majorLabel,
            });

            (major.near_root_cause_category || []).forEach((near, nIdx) => {
              const nearLabel = getLabel(near, ["near_root_cause_category", "name", "title", "label"]);
              if (!nearLabel) return;
              const nearId = `near_${near.near_root_cause_category_id || `${mIdx}_${sIdx}_${majIdx}_${nIdx}`}`;
              nodesList.push({
                id: nearId,
                type: "near_root_cause_category",
                parentNodeId: majorId,
                title: nearLabel,
              });

              (near.intermediate_cause_category || []).forEach((inter, iIdx) => {
                const interLabel = getLabel(inter, ["intermediate_cause_category", "name", "title", "label"]);
                if (!interLabel) return;
                const interId = `inter_${inter.intermediate_cause_category_id || `${mIdx}_${sIdx}_${majIdx}_${nIdx}_${iIdx}`}`;
                nodesList.push({
                  id: interId,
                  type: "intermediate_cause_category",
                  parentNodeId: nearId,
                  title: interLabel,
                });
              });
            });
          });
        });
      });
    } else {
      // Root Cause Type Hierarchy (Root Cause Analysis)
      const rootCauseTypes = contributingFactor?.root_cause_type || step4Data?.root_cause_type || [];
      if (!rootCauseTypes.length) return nodesList;

      const rootId = "root_rca";
      nodesList.push({
        id: rootId,
        type: "root",
        parentNodeId: null,
        title: "Root Cause Analysis",
      });

      rootCauseTypes.forEach((typeItem, tIdx) => {
        const typeLabel = getLabel(typeItem, ["root_cause_type", "name", "title", "label"]);
        if (!typeLabel) return;
        const typeId = `rct_${typeItem.root_cause_type_id || tIdx}`;
        nodesList.push({
          id: typeId,
          type: "root_cause_type",
          parentNodeId: rootId,
          title: typeLabel,
        });

        (typeItem.root_cause || []).forEach((rcItem, rcIdx) => {
          const rcLabel = getLabel(rcItem, ["root_cause", "name", "title", "label"]);
          if (!rcLabel) return;
          const rcId = `rc_${rcItem.root_cause_id || `${tIdx}_${rcIdx}`}`;
          nodesList.push({
            id: rcId,
            type: "root_cause",
            parentNodeId: typeId,
            title: rcLabel,
          });
        });
      });
    }

    return nodesList;
  }, [step4Data, isRoot, rootName]);

  // Build parent-to-children map
  const childrenMap = useMemo(() => {
    const map = {};
    hierarchyNodes.forEach((node) => {
      if (node.parentNodeId) {
        if (!map[node.parentNodeId]) map[node.parentNodeId] = [];
        map[node.parentNodeId].push(node.id);
      }
    });
    return map;
  }, [hierarchyNodes]);

  const toggleCollapse = useCallback((nodeId) => {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const handleExpandAll = () => setCollapsedNodes(new Set());
  const handleCollapseAll = () => {
    const parentIds = Object.keys(childrenMap);
    setCollapsedNodes(new Set(parentIds));
  };

  const { flowNodes, flowEdges } = useMemo(() => {
    const hiddenIds = new Set();
    collapsedNodes.forEach((collapsedId) => {
      getDescendantIds(collapsedId, childrenMap).forEach((id) => hiddenIds.add(id));
    });

    const visibleNodes = hierarchyNodes.filter((node) => !hiddenIds.has(node.id));

    const nodes = visibleNodes.map((node) => {
      const children = childrenMap[node.id] || [];
      const hasChildren = children.length > 0;
      const isCollapsed = collapsedNodes.has(node.id);
      const isRootNode = node.type === "root";
      const cfg = TYPE_CONFIG[node.type] || TYPE_CONFIG.root;

      return {
        id: node.id,
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
        data: {
          label: (
            <div
              className={`clc-node-card ${isRootNode ? "clc-root-pulse" : ""}`}
              style={{
                position: "relative",
                padding: "8px 12px",
                background: cfg.bg,
                border: `2px solid ${cfg.border}`,
                borderRadius: "8px",
                minWidth: "180px",
                maxWidth: "240px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.06)",
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: cfg.color,
                  textTransform: "uppercase",
                  marginBottom: "2px",
                  letterSpacing: "0.5px",
                }}
              >
                {cfg.label}
              </div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1F2937",
                  wordBreak: "break-word",
                  lineHeight: "1.2",
                }}
              >
                {node.title}
              </div>

              {hasChildren && (
                <button
                  type="button"
                  className="clc-toggle-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCollapse(node.id);
                  }}
                  style={{
                    position: "absolute",
                    bottom: "-12px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: isCollapsed ? "#10B981" : "#EF4444",
                    color: "#fff",
                    border: "2px solid #fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    zIndex: 10,
                  }}
                  title={isCollapsed ? "Expand branch" : "Collapse branch"}
                >
                  {isCollapsed ? "+" : "−"}
                </button>
              )}
            </div>
          ),
        },
      };
    });

    const edges = [];
    visibleNodes.forEach((node) => {
      if (node.parentNodeId && !hiddenIds.has(node.parentNodeId)) {
        const cfg = TYPE_CONFIG[node.type] || TYPE_CONFIG.root;
        edges.push({
          id: `e_${node.parentNodeId}_${node.id}`,
          source: node.parentNodeId,
          target: node.id,
          type: "smoothstep",
          animated: true,
          style: { stroke: cfg.border, strokeWidth: 2 },
        });
      }
    });

    const layouted = getLayoutedElements(nodes, edges);
    return { flowNodes: layouted.nodes, flowEdges: layouted.edges };
  }, [hierarchyNodes, childrenMap, collapsedNodes, toggleCollapse]);

  if (!hierarchyNodes.length) {
    return null;
  }

  // Active legends present in current chart
  const activeTypes = Array.from(new Set(hierarchyNodes.map((n) => n.type)));

  return (
    <div className="w-100 my-2" style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px" }}>
      <style>{animationStyles}</style>

      {/* Legend & Header controls (Matching provided color bar legend style) */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div className="d-flex flex-wrap align-items-center gap-4">
          {activeTypes.map((typeKey) => {
            const cfg = TYPE_CONFIG[typeKey];
            if (!cfg) return null;
            return (
              <div key={typeKey} className="d-flex align-items-center gap-2">
                <span
                  style={{
                    display: "inline-block",
                    width: "32px",
                    height: "16px",
                    borderRadius: "3px",
                    background: cfg.badgeColor,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                />
                <span style={{ fontSize: "13px", color: "#555555", fontWeight: 500 }}>
                  {cfg.label}
                </span>
              </div>
            );
          })}
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary px-3"
            style={{ borderRadius: "6px", fontSize: "12px", fontWeight: 500 }}
            onClick={handleCollapseAll}
          >
            Collapse All
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger px-3"
            style={{ borderRadius: "6px", fontSize: "12px", fontWeight: 500 }}
            onClick={handleExpandAll}
          >
            Expand All
          </button>
        </div>
      </div>

      {/* Flow Canvas Container */}
      <div style={{ height: "400px", width: "100%", background: "#FFFFFF", borderRadius: "8px", border: "1px solid #F3F4F6" }}>
        <ReactFlow nodes={flowNodes} edges={flowEdges} fitView attributionPosition="bottom-right">
          <Background color="#CBD5E1" gap={16} size={1} />
          <Controls />
        </ReactFlow>
      </div>
    </div>
  );
}
